// Trades (internal/finishing) — trigger sets for carpentry/joinery, plastering,
// painting & decorating, floor laying and wall/floor tiling. Keyed by report slug.
// Mirrors launch.ts tone; shared questions/constants imported from ./shared.

import type { TriggerQuestion } from "./types";
import {
  ASBESTOS,
  OCCUPIED,
  WAH,
  SILICA,
  MANUAL_HANDLING,
  HAVS,
  ACCESS_OPTIONS,
  HOT_WORK_PERMIT_Q,
  COSHH_DETAIL_QS,
  RESCUE_Q,
  PRE2000_Q,
} from "./shared";

// Minted hazard ids specific to this batch (reused across reports where the
// hazard is conceptually the same).
const COSHH_GENERAL = "haz-coshh-coshh-hazardou-substance-exposure";
const HOT_WORK_FIRE = "haz-hot-works-fire-from-hot-work-and-ignition-of-combustible";

// Painting-specific
const SOLVENT_SPRAY = "haz-trades-finishing-solvent-isocyanate-spray-exposure";
const LEAD_PAINT = "haz-trades-finishing-lead-paint-dust-exposure";

// Flooring-specific
const FLAMMABLE_ADHESIVE = "haz-trades-finishing-flammable-adhesive-vapour";

export const TRADES_INTERNAL_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "carpentry-joinery": [
    {
      key: "woodType",
      label: "Will hardwood (or MDF/composite) be cut or sanded?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Hardwood / composite board is cut or sanded — hardwood dust is a carcinogen (WEL 3 mg/m3); on-tool extraction and FFP3 apply." },
        no: { scopeLine: "Softwood only — wood dust (WEL 5 mg/m3) is still controlled at source with extraction and FFP3; no change to dust controls." },
      },
    },
    {
      key: "height",
      label: "Is any first/second-fix work done above safe reach (ceilings, high fixing)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [WAH] },
        no: { hazardsOff: [WAH], scopeLine: "No work above safe reach — all fixing is at or below shoulder height from the floor; podium/hop-up access is not required." },
      },
      notSure: { hazardsOn: [WAH], failCheck: "Work-at-height requirement confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "accessMethod",
            label: "What access equipment will be used?",
            kind: "choice",
            options: ACCESS_OPTIONS,
            effects: Object.fromEntries(ACCESS_OPTIONS.map((o) => [o, { scopeLine: `Access for work at height: ${o}.` }])),
          },
        ],
      },
    },
    {
      key: "adhesives",
      label: "Will solvent-based adhesives, contact adhesives or PU foams be used?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [COSHH_GENERAL, HOT_WORK_FIRE], permitsAdd: ["Hot-work permit where solvent products are used near ignition sources"] },
        no: {
          hazardsOff: [COSHH_GENERAL, HOT_WORK_FIRE],
          permitsRemove: ["Hot-work permit where solvent products are used near ignition sources"],
          emergencyOff: ["Fire involving adhesives"],
          sequenceOff: [6],
          scopeLine: "No solvent-based adhesives or PU foams — fixing is mechanical/cold; COSHH adhesive and ignition controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [COSHH_GENERAL, HOT_WORK_FIRE], failCheck: "Adhesive/solvent use confirmed" },
      followUps: { when: "yes", questions: COSHH_DETAIL_QS },
    },
    {
      key: "vibration",
      label: "Will routers, sanders or impact drivers be used for prolonged periods?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [HAVS] },
        no: { hazardsOff: [HAVS], scopeLine: "No prolonged power-tool use — hand-arm vibration exposure stays well below the EAV; HAVS surveillance is not triggered." },
      },
      notSure: { hazardsOn: [HAVS], failCheck: "Vibration exposure confirmed" },
    },
    PRE2000_Q([ASBESTOS], [ASBESTOS]),
    {
      key: "patRecord",
      label: "Are all portable powered tools PAT/inspection records in date?",
      kind: "text",
      placeholder: "e.g. PAT tested Mar 2026, next due Mar 2027",
      requiredCheck: "PAT / tool inspection record confirmed",
      effects: { "*": { scopeLine: "Portable tool inspection: {answer}." } },
    },
  ],

  plastering: [
    {
      key: "ceilingWork",
      label: "Is ceiling or high-wall work done from podiums or hop-ups?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [WAH] },
        no: { hazardsOff: [WAH], scopeLine: "Wall work within safe reach only — no podium/hop-up access required for ceilings or high walls." },
      },
      notSure: { hazardsOn: [WAH], failCheck: "Low-level access requirement confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "accessChoice",
            label: "Which low-level access will be used?",
            kind: "choice",
            options: ["Proprietary podium steps (guard rails)", "Hop-up / trestle (risk-assessed)", "Stilts (justified, site rules permitting)"],
            effects: {
              "Proprietary podium steps (guard rails)": { scopeLine: "Ceiling access: proprietary podium steps with guard rails, inspected before use." },
              "Hop-up / trestle (risk-assessed)": { scopeLine: "Ceiling access: hop-ups/trestles on firm level ground, one operative at a time, no over-reaching." },
              "Stilts (justified, site rules permitting)": { scopeLine: "Stilts are used only where site rules permit and justified by risk assessment, with a clear floor area, training and supervision; podiums remain the preferred method." },
            },
          },
        ],
      },
    },
    {
      key: "beadGrinding",
      label: "Will metal beads be cut with an angle grinder (rather than snips)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [HOT_WORK_FIRE], permitsAdd: ["Hot-work permit where beads are cut with a grinder near combustibles"] },
        no: {
          hazardsOff: [HOT_WORK_FIRE],
          permitsRemove: ["Hot-work permit where beads are cut with a grinder near combustibles"],
          emergencyOff: ["Grinding spark fire"],
          scopeLine: "Beads are trimmed with tin snips only — no grinding, so hot-work/spark controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [HOT_WORK_FIRE], failCheck: "Bead-cutting method confirmed" },
      followUps: { when: "yes", questions: [HOT_WORK_PERMIT_Q] },
    },
    PRE2000_Q([ASBESTOS], [ASBESTOS]),
    {
      key: "dustControl",
      label: "Is bag-emptying and mixing done with FFP3 and ventilation (no dry-sweeping)?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Mixing dust controlled: bags opened low and slow in a ventilated area, FFP3 worn, clean-down by vacuum/damp method only." },
        no: { failCheck: "Mixing dust controls (FFP3, ventilation, no dry-sweeping) confirmed" },
      },
      notSure: { failCheck: "Mixing dust controls confirmed" },
    },
  ],

  "painting-decorating": [
    {
      key: "spraying",
      label: "Will any coatings be spray-applied?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [SOLVENT_SPRAY], permitsAdd: ["Spraying permit / area isolation where solvent or isocyanate spraying is carried out"] },
        no: {
          hazardsOff: [SOLVENT_SPRAY],
          permitsRemove: ["Spraying permit / area isolation where solvent or isocyanate spraying is carried out"],
          scopeLine: "Brush and roller application only — no spraying, so spray-mist RPE/isolation controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [SOLVENT_SPRAY], failCheck: "Spray application confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "sprayProduct",
            label: "Is the product two-pack / isocyanate (vs single-pack solvent)?",
            kind: "yesno",
            effects: {
              yes: { scopeLine: "Two-pack/isocyanate spraying — air-fed RPE, area isolation and health surveillance for sprayers; others excluded from the zone." },
              no: { scopeLine: "Single-pack solvent spraying — correct organic-vapour filter RPE in a ventilated, isolated area." },
            },
            notSure: { failCheck: "Spray product type (isocyanate) confirmed" },
          },
        ],
      },
    },
    {
      key: "coshhProducts",
      label: "Will solvent-based paints, varnishes or fillers be used?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [COSHH_GENERAL] },
        // Paints are COSHH substances regardless of base (skin contact, prep
        // sanding dust) — "no" narrows the scope but never removes the hazard.
        no: {
          scopeLine: "Water-based products only — solvent-vapour controls are not required; standard COSHH skin/eye protection and SDS checks still apply.",
        },
      },
      notSure: { hazardsOn: [COSHH_GENERAL], failCheck: "Product type (solvent vs water-based) confirmed" },
      followUps: { when: "yes", questions: COSHH_DETAIL_QS },
    },
    {
      key: "flammableStorage",
      label: "Will flammable solvents/thinners be stored or used on site?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [HOT_WORK_FIRE] },
        no: {
          hazardsOff: [HOT_WORK_FIRE],
          emergencyOff: ["Fire or explosion involving solvents"],
          scopeLine: "No flammable solvents on site — DSEAR flammable-storage and soaked-rag controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [HOT_WORK_FIRE], failCheck: "Flammable solvent presence confirmed" },
    },
    {
      key: "height",
      label: "Is work at height required (stairwells, landings, ceilings, external)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [WAH] },
        no: { hazardsOff: [WAH], scopeLine: "All work within safe reach from the floor — no stairwell/ceiling/external access at height for this task." },
      },
      notSure: { hazardsOn: [WAH], failCheck: "Work-at-height requirement confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "accessMethod",
            label: "What access equipment will be used?",
            kind: "choice",
            options: ["Class 1 stepladder (footed)", "Proprietary stairwell platform", "Podium step", "Mobile tower (PASMA)"],
            effects: {
              "Class 1 stepladder (footed)": { scopeLine: "Access: Class 1 stepladder, footed and inspected — short-duration work only." },
              "Proprietary stairwell platform": { scopeLine: "Access: proprietary stairwell platform — no single ladder bridging a stairwell." },
              "Podium step": { scopeLine: "Access: podium step with guard rails." },
              "Mobile tower (PASMA)": { scopeLine: "Access: mobile tower assembled and used by PASMA-trained operatives." },
            },
          },
          RESCUE_Q,
        ],
      },
    },
    {
      key: "occupied",
      label: "Are the premises occupied during the work?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OCCUPIED], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [OCCUPIED], scopeLine: "Premises unoccupied during the works — occupant-interface controls are not required for this task." },
      },
      notSure: { hazardsOn: [OCCUPIED], failCheck: "Occupancy during the works confirmed" },
    },
    PRE2000_Q([ASBESTOS, LEAD_PAINT], [ASBESTOS, LEAD_PAINT]),
  ],

  "floor-laying": [
    {
      key: "adhesiveType",
      label: "What type of bonding will be used?",
      kind: "choice",
      options: ["Solvent-based contact adhesive", "Water-based / low-VOC adhesive", "Loose-lay / click system (no adhesive)"],
      effects: {
        "Solvent-based contact adhesive": { hazardsOn: [FLAMMABLE_ADHESIVE], scopeLine: "Bonding with solvent-based contact adhesive — room ventilated and all ignition sources removed before opening the tin (DSEAR)." },
        "Water-based / low-VOC adhesive": { hazardsOff: [FLAMMABLE_ADHESIVE], permitsRemove: ["Hot-work prohibition / ignition-source control where flammable adhesives are in use (DSEAR)"], emergencyOff: ["Solvent-vapour overexposure or fire"], scopeLine: "Bonding with water-based/low-VOC adhesive — flammable-vapour DSEAR controls are not required for this task." },
        "Loose-lay / click system (no adhesive)": { hazardsOff: [FLAMMABLE_ADHESIVE, COSHH_GENERAL], permitsRemove: ["Hot-work prohibition / ignition-source control where flammable adhesives are in use (DSEAR)"], emergencyOff: ["Solvent-vapour overexposure or fire"], scopeLine: "Loose-lay / click system — no flooring adhesive used, so adhesive COSHH and DSEAR controls are not required for this task." },
      },
    },
    {
      key: "subfloorGrinding",
      label: "Will the concrete/screed subfloor be ground or scarified (silica dust)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [SILICA], permitsAdd: ["Dust/grinding area agreement where on-tool extraction water is supplied"] },
        no: {
          hazardsOff: [SILICA],
          permitsRemove: ["Dust/grinding area agreement where on-tool extraction water is supplied"],
          sequenceOff: [2],
          scopeLine: "No grinding or scarifying of the subfloor — silica dust controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [SILICA], failCheck: "Subfloor grinding requirement confirmed" },
    },
    {
      key: "latexPrimer",
      label: "Will latex/levelling compound or primer be mixed and applied?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [COSHH_GENERAL] },
        no: {
          hazardsOff: [COSHH_GENERAL],
          scopeLine: "No latex/levelling compound or primer — compound dust and skin-contact COSHH controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [COSHH_GENERAL], failCheck: "Latex/primer use confirmed" },
      followUps: { when: "yes", questions: COSHH_DETAIL_QS },
    },
    {
      key: "occupied",
      label: "Are the premises occupied during the work?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OCCUPIED], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [OCCUPIED], scopeLine: "Premises unoccupied during the works — occupant fume/dust interface controls are not required for this task." },
      },
      notSure: { hazardsOn: [OCCUPIED], failCheck: "Occupancy during the works confirmed" },
    },
    PRE2000_Q([ASBESTOS], [ASBESTOS]),
    {
      key: "ventilationConfirm",
      label: "Is the room ventilation confirmed before bonding (issuer/arrangement)?",
      kind: "text",
      placeholder: "e.g. windows open both ends + mechanical extract, confirmed by site supervisor",
      requiredCheck: "Bonding-area ventilation arrangement recorded",
      effects: { "*": { scopeLine: "Ventilation before bonding: {answer}." } },
    },
  ],

  tiling: [
    {
      key: "cuttingMethod",
      label: "How will tiles be cut?",
      kind: "choice",
      options: ["Water-fed wet saw", "Score-and-snap only", "Dry cutting with grinder (last resort, on-tool extraction)"],
      effects: {
        "Water-fed wet saw": { scopeLine: "Cutting by water-fed wet saw with suppression working — porcelain is never dry-cut with a grinder." },
        "Score-and-snap only": { hazardsOff: [SILICA], permitsRemove: ["Wet-cutting / dust area agreed where the saw needs a water supply"], scopeLine: "Straight cuts by score-and-snap only — no powered cutting, so wet-cutting/silica dust controls are not required for this task." },
        "Dry cutting with grinder (last resort, on-tool extraction)": { scopeLine: "Dry cutting only where unavoidable, with on-tool extraction and face-fit FFP3, cleaned by vacuum not sweeping." },
      },
    },
    {
      key: "silicaConfirm",
      label: "Will any powered tile cutting take place (wet saw or grinder)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [SILICA], permitsAdd: ["Wet-cutting / dust area agreed where the saw needs a water supply"] },
        no: { hazardsOff: [SILICA], permitsRemove: ["Wet-cutting / dust area agreed where the saw needs a water supply"], scopeLine: "No powered cutting — silica dust controls are not required for this task." },
      },
      notSure: { hazardsOn: [SILICA], failCheck: "Powered cutting requirement confirmed" },
    },
    {
      key: "largeFormat",
      label: "Will large-format tiles or heavy tile boxes be handled?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [MANUAL_HANDLING], permitsAdd: ["Manual-handling assessment record for large-format tiling"] },
        no: { hazardsOff: [MANUAL_HANDLING], permitsRemove: ["Manual-handling assessment record for large-format tiling"], scopeLine: "Standard-size tiles only — large-format handling aids and a separate manual-handling assessment are not required for this task." },
      },
      notSure: { hazardsOn: [MANUAL_HANDLING], failCheck: "Large-format handling requirement confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "liftMethod",
            label: "How will large-format tiles be handled?",
            kind: "choice",
            options: ["Vacuum/suction lifters + transport frame", "Two-person manual handling", "Both as appropriate"],
            effects: {
              "Vacuum/suction lifters + transport frame": { scopeLine: "Large-format handling: vacuum/suction lifters and a transport frame." },
              "Two-person manual handling": { scopeLine: "Large-format handling: two-person lift, load kept close, route and rest points planned." },
              "Both as appropriate": { scopeLine: "Large-format handling: suction lifters and two-person handling as appropriate to the tile." },
            },
          },
        ],
      },
    },
    {
      key: "occupied",
      label: "Are the premises occupied during the work?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OCCUPIED], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [OCCUPIED], scopeLine: "Premises unoccupied during the works — occupant dust/access interface controls are not required for this task." },
      },
      notSure: { hazardsOn: [OCCUPIED], failCheck: "Occupancy during the works confirmed" },
    },
    PRE2000_Q([ASBESTOS], [ASBESTOS]),
    {
      key: "wetCutConfirm",
      label: "Is the wet saw water supply confirmed and topped up during cutting?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Wet-saw water suppression confirmed and topped up throughout cutting — no dry-cutting of porcelain." },
        no: { failCheck: "Wet-cutting water suppression confirmed" },
      },
      notSure: { failCheck: "Wet-cutting water suppression confirmed" },
    },
  ],
};
